/**
 * FieldHelpButton — small [?] inline button that opens the AI assistant
 * panel pre-filled with a question about a specific form field.
 *
 * Usage:
 *   import { FieldHelpButton } from "@/components/FieldHelpButton";
 *
 *   <label>A-Number <FieldHelpButton question="What is an A-Number?" fieldKey="a_number" /></label>
 *
 * The button triggers a custom DOM event ("open-ai-panel") that
 * AiAssistantPanel listens for so it can open itself and pre-fill the input.
 * This keeps the two components decoupled — no prop drilling needed.
 *
 * fieldKey (optional) — when provided, the backend injects field-level context
 * into the AI system prompt so the answer is more precisely targeted.
 * Keys must match entries in services/api/internal/ai/field_context.go.
 */

interface Props {
  /** The question text to pre-fill in the chat input. */
  question: string;
  /**
   * Optional form-response field key (e.g. "a_number", "ssn").
   * When set, the AI assistant receives additional field-specific context.
   */
  fieldKey?: string;
  /** Accessible label (defaults to "Help for this field"). */
  ariaLabel?: string;
}

export function FieldHelpButton({ question, fieldKey, ariaLabel = "Help for this field" }: Props) {
  function handleClick(e: React.MouseEvent) {
    e.preventDefault();
    window.dispatchEvent(
      new CustomEvent("open-ai-panel", { detail: { question, fieldKey } }),
    );
  }

  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={handleClick}
      className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold text-slate-400 border border-slate-300 hover:text-blue-600 hover:border-blue-400 hover:bg-blue-50 transition-colors ml-1 align-middle cursor-help"
    >
      ?
    </button>
  );
}
